import React, { useState, useEffect } from 'react'
import axios from 'axios'
import SecureLS from 'secure-ls'
import { API_CONFIG } from '../../config/api'

const ls = new SecureLS({ encodingType: 'aes' })

/**
 * FamilyMemberEditBasic - Simple, focused editing interface for core information
 *
 * Design based on family-member-edit-mockup.svg
 * Features:
 * - Profile picture upload
 * - Essential fields: name, relationship, birth date, email, phone, biography
 * - Large input fields (45px height)
 * - Clear save/cancel buttons
 * - Delete member link (subtle)
 */
function FamilyMemberEditBasic({ memberData, familyId, onSave, onCancel, onAdvanced, onDelete, onBack }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    relationship: 'member',
    date_of_birth: '',
    email: '',
    phone: '',
    bio: ''
  })

  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [profilePhoto, setProfilePhoto] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [photoUploadSuccess, setPhotoUploadSuccess] = useState(false)

  // Helper to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return ''
    // If it's already in YYYY-MM-DD format, return as-is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue
    }
    // Try to parse and format the date
    try {
      // For date-only strings, append T12:00:00 to avoid timezone issues
      let date
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
        date = new Date(dateValue.substring(0, 10) + 'T12:00:00')
      } else {
        date = new Date(dateValue)
      }
      if (isNaN(date.getTime())) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    } catch (e) {
      console.error('Error formatting date:', e)
      return ''
    }
  }

  // Initialize form data from memberData
  useEffect(() => {
    if (memberData) {
      setFormData({
        first_name: memberData.first_name || '',
        last_name: memberData.last_name || '',
        relationship: memberData.role || 'member',
        date_of_birth: formatDateForInput(memberData.date_of_birth),
        email: memberData.email || '',
        phone: memberData.phone || '',
        bio: memberData.bio || ''
      })
      setProfilePhoto(memberData.profile_photo_url || null)
    }
  }, [memberData])

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Get file extension
      const fileName = file.name.toLowerCase()
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'))

      // Validate file type - accept images and HEIC/HEIF formats
      const isImage = file.type.startsWith('image/')
      const isHEIC = ['.heic', '.heif'].includes(fileExtension)

      if (!isImage && !isHEIC) {
        setError('Please select an image file (JPG, PNG, HEIC, etc.)')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB')
        return
      }

      setSelectedFile(file)

      // Create a preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        setProfilePhoto(e.target.result)
      }
      reader.readAsDataURL(file)

      // Upload immediately
      uploadPhoto(file)
    }
  }

  const uploadPhoto = async (file) => {
    setIsUploadingPhoto(true)
    setPhotoUploadSuccess(false)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const token = ls.get('authToken')
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/families/${familyId}/members/${memberData.id}/profile-photo/`,
        formData,
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      if (response.data.success) {
        setProfilePhoto(response.data.profile_photo_url)
        setPhotoUploadSuccess(true)
        // Hide success message after 3 seconds
        setTimeout(() => setPhotoUploadSuccess(false), 3000)
      } else {
        setError(response.data.error || 'Failed to upload photo')
        setProfilePhoto(memberData.profile_photo_url || null)
      }
    } catch (err) {
      console.error('Error uploading photo:', err)
      setError('Failed to upload photo')
      setProfilePhoto(memberData.profile_photo_url || null)
    } finally {
      setIsUploadingPhoto(false)
      setSelectedFile(null)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validate required fields
    if (!formData.first_name.trim()) {
      setError('First name is required')
      return
    }

    if (!formData.last_name.trim()) {
      setError('Last name is required')
      return
    }

    setIsSaving(true)

    try {
      const result = await onSave(formData)

      if (!result.success) {
        setError(result.error || 'Failed to save changes')
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setIsSaving(false)
    }
  }

  const fullName = `${formData.first_name} ${formData.last_name}`.trim() || 'Unknown'
  const initials = (formData.first_name?.[0] || '') + (formData.last_name?.[0] || '')

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Header */}
      <div className="bg-[#556B2F] text-white py-5 px-8">
        <h1 className="text-2xl font-bold">One Generation to Next</h1>
      </div>

      {/* Back link */}
      <div className="px-8 py-4">
        <button
          onClick={onBack}
          className="text-[#E65100] text-base hover:underline"
        >
          ← Back to Family Members
        </button>
      </div>

      {/* Main Content Container */}
      <div className="max-w-6xl mx-auto px-8 pb-12">
        <div className="bg-[#F2F2F2] rounded-xl border border-[#CCCCCC] shadow-md p-8">

          {/* Title Section */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#333333] mb-2">Edit Family Member</h2>
            <p className="text-lg text-[#666666]">Update information about this family member</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="flex gap-8 mb-8">
              {/* Profile Picture Section */}
              <div className="flex-shrink-0">
                <div className="relative mb-4">
                  {profilePhoto ? (
                    <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-[#6B8E23]">
                      <img
                        src={profilePhoto.startsWith('/') ? `${API_CONFIG.BASE_URL}${profilePhoto}` : profilePhoto}
                        alt={`${fullName} profile`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-[#C8E6C9] border-2 border-[#6B8E23] flex items-center justify-center">
                      <span className="text-4xl font-bold text-[#333333]">{initials}</span>
                    </div>
                  )}

                  {/* Active indicator */}
                  <div className="absolute top-0 right-0 w-3 h-3 bg-[#4CAF50] rounded-full"></div>
                </div>

                <input
                  type="file"
                  id="profile-photo-input"
                  accept="image/*,.heic,.heif"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <div>
                  <button
                    type="button"
                    onClick={() => document.getElementById('profile-photo-input').click()}
                    disabled={isUploadingPhoto}
                    className="w-32 px-4 py-2 bg-[#6B8E23] text-white text-xs font-medium rounded-md hover:bg-[#556B2F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploadingPhoto ? 'Uploading...' : photoUploadSuccess ? '✓ Saved!' : 'Change Photo'}
                  </button>
                  <p className="text-xs text-[#666666] mt-1 text-center">Saves automatically</p>
                </div>
              </div>

              {/* Form Fields */}
              <div className="flex-1 space-y-6">
                {/* Full Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-[#333333] mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-base bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                      style={{ height: '45px' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#333333] mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-base bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                      style={{ height: '45px' }}
                    />
                  </div>
                </div>

                {/* Relationship */}
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">
                    Relationship *
                  </label>
                  <select
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 text-base bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                    style={{ height: '45px' }}
                  >
                    <option value="member">Family member</option>
                    <option value="parent">Parent</option>
                    <option value="child">Child</option>
                    <option value="sibling">Sibling</option>
                    <option value="spouse">Spouse</option>
                    <option value="grandparent">Grandparent</option>
                    <option value="grandchild">Grandchild</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Birth Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-[#333333] mb-2">
                      Birth Date
                    </label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-base bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                      style={{ height: '45px' }}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                    style={{ height: '45px' }}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-3 text-base bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
                    style={{ height: '45px' }}
                  />
                </div>
              </div>
            </div>

            {/* Biography Section */}
            <div className="mb-8">
              <label className="block text-sm font-bold text-[#333333] mb-1">
                Biography
              </label>
              <p className="text-xs text-[#666666] mb-2">
                Share a few words about this family member
              </p>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows="4"
                placeholder="Tell their story, share memories, or describe what makes them special..."
                className="w-full px-4 py-3 text-sm text-[#333333] bg-white border border-[#CCCCCC] rounded-md focus:outline-none focus:ring-2 focus:ring-[#556B2F] resize-none"
              />
            </div>

            {/* Divider Line */}
            <div className="border-t border-[#CCCCCC] mb-6"></div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <div className="flex gap-4 items-center">
                {/* Cancel Button */}
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-6 py-3 bg-[#F2F2F2] text-[#333333] text-base font-medium border border-[#CCCCCC] rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>

                {/* Required fields indicator */}
                <span className="text-xs text-[#666666]">* Required fields</span>
              </div>

              <div className="flex gap-4 items-center">
                {/* Delete Link */}
                <button
                  type="button"
                  onClick={onDelete}
                  className="text-[#E65100] text-sm hover:underline"
                >
                  Delete Member
                </button>

                {/* Advanced Settings Link */}
                {onAdvanced && (
                  <button
                    type="button"
                    onClick={onAdvanced}
                    className="text-[#556B2F] text-sm hover:underline"
                  >
                    Advanced Settings →
                  </button>
                )}

                {/* Save Button */}
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-3 bg-[#556B2F] text-white text-base font-medium rounded-lg hover:bg-[#4A5D23] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default FamilyMemberEditBasic
